import { useState, useEffect, useRef, type CSSProperties } from "react";
import type { LibraryNode } from "../../../lib/types";
import { useCreateFolder } from "../../../lib/hooks/use-kuvio";
import { showToast } from "../primitives";

interface NewFolderModalProps {
  open: boolean;
  onClose: () => void;
  /** Parent the folder is created under. */
  parentId: string;
  /** Parent display name, shown as context. */
  parentName?: string;
  onCreated?: (node: LibraryNode) => void;
}

const DESCRIPTION_LIMIT = 200;

export function NewFolderModal({ open, onClose, parentId, parentName, onCreated }: NewFolderModalProps) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const nameRef = useRef<HTMLInputElement>(null);
  const createFolder = useCreateFolder();

  useEffect(() => {
    if (open) {
      setName("");
      setDescription("");
      setTimeout(() => nameRef.current?.focus(), 50);
    }
  }, [open]);

  if (!open) return null;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    createFolder.mutate(
      {
        name: name.trim(),
        description: description.trim() || undefined,
        parent_id: parentId || undefined,
      },
      {
        onSuccess: (node) => {
          showToast(`Folder '${node.name}' created`);
          onCreated?.(node);
          onClose();
        },
        onError: (err) => {
          const msg = err instanceof Error ? err.message : "Something went wrong";
          showToast(`Couldn't create folder: ${msg}`, "error", true);
        },
      },
    );
  }

  const overlay: CSSProperties = {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.3)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 100,
    fontFamily: "var(--font-sans)",
  };

  const modal: CSSProperties = {
    background: "var(--color-background-primary)",
    border: "var(--kuvio-border-default)",
    borderRadius: "var(--border-radius-lg)",
    padding: 24,
    width: 420,
    maxWidth: "90vw",
  };

  const field: CSSProperties = { marginBottom: 16 };

  const label: CSSProperties = {
    display: "block",
    fontSize: 12,
    fontWeight: 500,
    color: "var(--color-text-secondary)",
    marginBottom: 4,
  };

  const input: CSSProperties = {
    width: "100%",
    fontSize: 13,
    padding: "8px 10px",
    border: "var(--kuvio-border-default)",
    borderRadius: "var(--border-radius-sm)",
    background: "var(--color-background-primary)",
    color: "var(--color-text-primary)",
    fontFamily: "var(--font-sans)",
  };

  const folderIcon: CSSProperties = {
    width: 40,
    height: 40,
    borderRadius: "var(--border-radius-md)",
    background: "var(--color-background-secondary)",
    border: "var(--kuvio-border-default)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "var(--color-text-secondary)",
    fontSize: 20,
    flexShrink: 0,
  };

  return (
    <div style={overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={modal} role="dialog" aria-label="New folder">
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
          <div style={folderIcon}>
            <i className="ti ti-folder" aria-hidden="true" />
          </div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 500, color: "var(--color-text-primary)" }}>
              New folder
            </div>
            <div style={{ fontSize: 12, color: "var(--color-text-tertiary)" }}>
              {parentName ? `Organise content inside ${parentName}` : "A container for pure organisation"}
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={field}>
            <label style={label}>Folder name</label>
            <input
              ref={nameRef}
              type="text"
              style={input}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Specifications"
              maxLength={80}
            />
          </div>

          <div style={field}>
            <label style={label}>Description</label>
            <input
              type="text"
              style={input}
              value={description}
              onChange={(e) => setDescription(e.target.value.slice(0, DESCRIPTION_LIMIT))}
              placeholder="What is this folder for? Optional — 200 chars max."
              maxLength={DESCRIPTION_LIMIT}
            />
          </div>

          <div
            style={{
              fontSize: 12,
              fontStyle: "italic",
              color: "var(--color-text-tertiary)",
              lineHeight: 1.5,
              marginTop: 4,
              marginBottom: 4,
              paddingTop: 12,
              borderTop: "var(--kuvio-border-default)",
            }}
          >
            Folders are pure organisation. If this node will have a role or persona you can act
            as, create a sub-agent instead — agents scope content <em>and</em> provide AI chat.
          </div>

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 20 }}>
            <button type="button" onClick={onClose} style={{ fontSize: 13, padding: "8px 14px" }}>
              Cancel
            </button>
            <button
              type="submit"
              data-variant="primary"
              disabled={!name.trim() || createFolder.isPending}
              style={{ fontSize: 13, padding: "8px 14px" }}
            >
              {createFolder.isPending ? "Creating..." : "Create folder"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
